import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { observer } from 'mobx-react-lite'
import { useStore } from '~/app/store'
import { Button, Card, DeleteSVG } from '~/shared/ui'
import { Input } from '~/shared/ui/input'
import { UserRole } from '~/features/roles'
import { UnlockSVG } from '~/shared/ui/SVG/unlockSVG'
import Spinner from '~/shared/ui/spinner'
import { ModalDeleteUser } from '~/features/user-edit/modal-delete'
import { BlocketSVG } from '~/shared/ui/SVG/blockedSVG'
import {
  Handler,
  LEGAL_ENTITY_GREY_SHOP,
  formatErrorForToast,
  useForm,
  useToast,
} from '~/shared/lib'
import { PrinterSettings } from '~/widgets/printer-settings'
import { useErrorHandler } from '~/shared/lib/hooks/use-error-handler'

export const UserEdit = observer(() => {
  const { t } = useTranslation(['common', 'profile'])
  const { usersStore, printerStore } = useStore()
  const { id } = useParams()
  const [openModal, setOpenModal] = useState(false)
  const toast = useToast()
  const { handleErrorWithToast } = useErrorHandler()

  const { editedUser, loaders, getUsersById, userProfile } = usersStore

  const {
    updatePrinterUser,
    setPrintersUser,
    printersUser,
    markPrinterFieldAsChanged,
  } = printerStore

  const [isShowPassword, setIsShowPassword] = useState({
    password: false,
    newPassword: false,
  })

  const navigate = useNavigate()

  useEffect(() => {
    const fetchUser = async () => {
      if (id) {
        try {
          const userFetch = await getUsersById(id)

          const hasPrinters = userFetch?.settings?.printers

          setPrintersUser(hasPrinters)
        } catch (error) {
          handleErrorWithToast(error)
        }
      }
    }

    fetchUser()
    return () => {
      usersStore.resetUser('editedUser')
      usersStore.setTempHandler([])
    }
  }, [setPrintersUser, getUsersById, id, usersStore, handleErrorWithToast])

  const togglePasswordVisibility = (type: keyof typeof isShowPassword) => {
    setIsShowPassword((prev) => ({
      ...prev,
      [type]: !prev[type],
    }))
  }

  const {
    handleSubmit,
    handleChange,
    errors,
    data: formData,
    setData,
  } = useForm<{ name: string; password: string; newPassword: string }>({
    initialValues: {
      name: '',
      password: '',
      newPassword: '',
    },
    validations: {
      name: {
        custom: {
          isValid: (value) => value.trim().length >= 1 && !/^\s/.test(value),
          message: t('profile:enterUserName'),
        },
      },
      password: {
        custom: {
          isValid: (value) => !value || value.length >= 8,
          message: t('profile:passwordMinLength'),
        },
      },
      newPassword: {
        custom: {
          isValid: (value, values) => !value || value === values.password,
          message: t('profile:passwordsMustMatch'),
        },
      },
    },
    onSubmit: async (data) => {
      try {
        if (usersStore.editedUser?.name !== data?.name && id) {
          if (id === userProfile?.id) {
            await usersStore.editProfile({ name: data.name })
          } else {
            await usersStore.editUser(id, data.name)
          }

          await usersStore.getUsers('init')
          toast.open(t('profile:profileUpdatedSuccess'), 'success')
        }
        if (id && data.newPassword) {
          if (id === userProfile?.id) {
            await usersStore.changeProfilePassword(data.newPassword)
          } else {
            await usersStore.changePassword(id, data.newPassword)
          }
          toast.open(t('profile:passwordUpdatedSuccess'), 'success')
        }
        usersStore.resetUser('editedUser')
        navigate('/settings')
      } catch (error) {
        toast.open(formatErrorForToast(error), 'danger')
        handleErrorWithToast(error)
      }
    },
  })

  useEffect(() => {
    if (editedUser?.name) {
      const newData = {
        ...formData,
        name: editedUser.name,
      }
      setData(newData)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editedUser, setData])

  useEffect(() => {
    if (handleSubmit) {
      const tempHandler = handleSubmit as Handler
      usersStore.setTempHandler([tempHandler])
    }
  }, [usersStore, handleSubmit])

  const handleChangePrinter = (key: string, value: string | number) => {
    markPrinterFieldAsChanged(key)
    updatePrinterUser(key, value)
  }

  return (
    <form onSubmit={handleSubmit}>
      <div className="flex gap-4">
        <Card className="flex w-[420px] h-full flex-col gap-4">
          <p className="text-[20px] text-[#242424]">
            {t('profile:profileInfo')}
          </p>

          <Input
            className="rounded"
            label={t('name')}
            type="text"
            id="nameEditUser"
            onChange={handleChange('name')}
            value={formData?.name ?? ''}
            labelType="floating"
            helperText={errors.name}
            status={errors.name ? 'error' : undefined}
            disabled={usersStore.isListLoading}
          />
          <Input
            className="rounded"
            label={t('email')}
            type="email"
            intent="disabled"
            value={editedUser?.email ?? ''}
            id="email"
            readOnly
            autoComplete="new-email"
            labelType="floating"
          />

          <p className="text-[20px] text-[#242424]">{t('password')}</p>
          <Input
            name="password"
            label={t('password')}
            type={isShowPassword.password ? 'text' : 'password'}
            onChange={handleChange('password')}
            id="password"
            labelType="floating"
            helperText={errors.password}
            status={errors.password ? 'error' : undefined}
            showPassword={() => togglePasswordVisibility('password')}
            autoComplete="new-password"
          />
          <Input
            name="newPassword"
            label={t('profile:repeatPassword')}
            type={isShowPassword.newPassword ? 'text' : 'password'}
            onChange={handleChange('newPassword')}
            id="newPassowrd"
            labelType="floating"
            helperText={errors.newPassword}
            status={errors.newPassword ? 'error' : undefined}
            showPassword={() => togglePasswordVisibility('newPassword')}
            autoComplete="new-password"
          />
        </Card>
        <UserRole />
        {userProfile?.legalEntity.id === LEGAL_ENTITY_GREY_SHOP && (
          <Card className="flex flex-col gap-4 w-[420px] pb-6">
            <PrinterSettings
              onChange={handleChangePrinter}
              selectedPrinters={printersUser}
              className="text-[20px] text-[#242424]"
            />
          </Card>
        )}
      </div>
      {editedUser && (
        <div className="mt-6 flex justify-end gap-4">
          {editedUser?.enabled ? (
            <Button
              intent="danger"
              onClick={async () => {
                try {
                  if (editedUser.id)
                    await usersStore.blockUser(editedUser.id, false)
                  toast.open(t('profileBlockedSuccess'), 'success')
                } catch (error) {
                  toast.open(formatErrorForToast(error), 'danger')
                  handleErrorWithToast(error)
                }
              }}
            >
              <BlocketSVG color="white" />
              {t('block')}
            </Button>
          ) : (
            <>
              <Button
                intent="yellow"
                className="w-[190px]"
                onClick={async () => {
                  try {
                    if (editedUser?.id)
                      await usersStore.blockUser(editedUser.id, true)
                    toast.open(t('profileUnblockedSuccess'), 'success')
                  } catch (error) {
                    toast.open(formatErrorForToast(error), 'danger')
                    handleErrorWithToast(error)
                  }
                }}
              >
                <UnlockSVG color="white" /> {t('unblock')}
              </Button>
              <Button
                intent="danger"
                className="w-[150px]"
                onClick={() => setOpenModal(true)}
              >
                {loaders.userDelete ? <Spinner /> : <DeleteSVG color="white" />}
                {t('delete')}
              </Button>
            </>
          )}
        </div>
      )}
      <ModalDeleteUser
        isOpen={openModal}
        id={editedUser?.id}
        onClose={(e) => {
          e.preventDefault()
          setOpenModal(false)
        }}
      />
    </form>
  )
})
